import CommandPalette from "@/app/dashboard/CommandPalette";
import EditionPortalPage from "@/app/dashboard/EditionPortalPage";
import LedgerPortalPage from "@/app/dashboard/LedgerPortalPage";
import PortalPage from "@/app/dashboard/PortalPage";
import WatchCreatePortalPage from "@/app/dashboard/WatchCreatePortalPage";
import { useAppContext } from "@/app/providers/AppContext";
import AuthCallback from "@/domains/auth/pages/AuthCallback";
import LoginPage from "@/domains/auth/pages/LoginPage";
import BillingCancelPage from "@/domains/billing/pages/BillingCancelPage";
import BillingSuccessPage from "@/domains/billing/pages/BillingSuccessPage";
import TickerDashboard from "@/domains/insights/pages/TickerDashboard";
import LandingPage from "@/landing/pages/LandingPage";
import PrivacyPage from "@/landing/pages/PrivacyPage";
import ErrorBoundary, { TabFallback } from "@/shared/components/ErrorBoundary";
import { useAttributionCapture } from "@/shared/attribution";
import { usePageTracking } from "@/shared/hooks/usePageTracking";
import { useEffect, type ReactNode } from "react";
import {
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigate,
} from "react-router-dom";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
function RequirePortalAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAppContext();
  const location = useLocation();

  if (loading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
function App() {
  const { isAuthenticated, loading } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  usePageTracking();
  useAttributionCapture();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      if (
        location.pathname === "/login" ||
        location.pathname === "/new/login"
      ) {
        navigate("/portal", { replace: true });
      }
    }
  }, [isAuthenticated, loading, location.pathname, navigate]);

  return (
    <ErrorBoundary section="app.root">
      <ScrollToTop />
      <CommandPalette />
      <Routes>
        <Route
          path="/"
          element={
            <ErrorBoundary section="landing" fallback={<TabFallback section="Landing" />}>
              <LandingPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="/login"
          element={
            <ErrorBoundary section="auth.login" fallback={<TabFallback section="Login" />}>
              <LoginPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="/portal"
          element={
            <RequirePortalAuth>
              <Navigate to="/portal/overview" replace />
            </RequirePortalAuth>
          }
        />
        <Route
          path="/portal/watches/new"
          element={
            <RequirePortalAuth>
              <WatchCreatePortalPage />
            </RequirePortalAuth>
          }
        />
        {/* JS-1060: the daily edition + archive. A dedicated route (like
            /portal/watches/new above) rather than /portal/:tabId, since an
            edition needs a DATE in its URL to be linkable/bookmarkable.
            FOLLOW-UP 6 (adversarial review): unlike the backend's FastAPI
            routes (which really do match in registration order -- see
            routes.py's /editions/today-before-/editions/{as_of} comment),
            React Router v6+ ranks routes by SPECIFICITY, not declaration
            order: a static segment ("edition") always outranks a dynamic
            one (":asOf") regardless of which is registered first, so
            "/portal/trade-ideas/edition" is guaranteed to match the literal
            route and never falls through to :asOf. The two routes are kept
            in this order purely for readability (mirrors the backend's own
            today/by-date pairing), not because order matters here. */}
        <Route
          path="/portal/trade-ideas/edition"
          element={
            <RequirePortalAuth>
              <EditionPortalPage />
            </RequirePortalAuth>
          }
        />
        <Route
          path="/portal/trade-ideas/edition/:asOf"
          element={
            <RequirePortalAuth>
              <EditionPortalPage />
            </RequirePortalAuth>
          }
        />
        {/* JS-1090b: the audit-grade outcome ledger + performance surface.
            Same static-segment-specificity reasoning as the edition routes
            above -- registered before /portal/:tabId so the literal segment
            always wins under React Router's specificity-based matching. */}
        <Route
          path="/portal/trade-ideas/ledger"
          element={
            <RequirePortalAuth>
              <LedgerPortalPage />
            </RequirePortalAuth>
          }
        />
        <Route
          path="/portal/:tabId"
          element={
            <RequirePortalAuth>
              <ErrorBoundary section="portal" fallback={<TabFallback section="Portal" />}>
                <PortalPage />
              </ErrorBoundary>
            </RequirePortalAuth>
          }
        />
        <Route
          path="/portal/auth/callback"
          element={
            <ErrorBoundary section="auth.callback" fallback={<TabFallback section="Authentication" />}>
              <AuthCallback />
            </ErrorBoundary>
          }
        />
        <Route
          path="/auth/callback"
          element={
            <ErrorBoundary section="auth.callback" fallback={<TabFallback section="Authentication" />}>
              <AuthCallback />
            </ErrorBoundary>
          }
        />
        <Route
          path="/ticker/:ticker"
          element={
            <ErrorBoundary section="ticker-dashboard" fallback={<TabFallback section="Ticker Dashboard" />}>
              <TickerDashboard />
            </ErrorBoundary>
          }
        />
        <Route
          path="/privacy"
          element={
            <ErrorBoundary section="privacy" fallback={<TabFallback section="Privacy" />}>
              <PrivacyPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="/checkout/success"
          element={
            <ErrorBoundary section="billing.success" fallback={<TabFallback section="Checkout" />}>
              <BillingSuccessPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="/checkout/cancel"
          element={
            <ErrorBoundary section="billing.cancel" fallback={<TabFallback section="Checkout" />}>
              <BillingCancelPage />
            </ErrorBoundary>
          }
        />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
